import { useRef, useState, useEffect, useCallback } from 'react'

interface CommunityPostCarouselProps {
  readonly imageUrls: readonly string[]
}

export function CommunityPostCarousel({ imageUrls }: CommunityPostCarouselProps) {
  const scrollerRef = useRef<HTMLDivElement | null>(null)
  const [activeSlideIndex, setActiveSlideIndex] = useState(0)

  const handleScroll = useCallback(() => {
    const scroller = scrollerRef.current
    if (!scroller) return
    const slideWidth = scroller.clientWidth
    if (slideWidth === 0) return
    const nextIndex = Math.round(scroller.scrollLeft / slideWidth)
    setActiveSlideIndex((current) => (current === nextIndex ? current : nextIndex))
  }, [])

  useEffect(() => {
    const scroller = scrollerRef.current
    if (!scroller) return
    scroller.addEventListener('scroll', handleScroll, { passive: true })
    return () => scroller.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  if (imageUrls.length === 0) return null

  const hasMultipleImages = imageUrls.length > 1

  return (
    <div className="relative w-full overflow-hidden rounded-lg bg-muted">
      <div
        ref={scrollerRef}
        className="flex w-full snap-x snap-mandatory overflow-x-auto hide-scrollbar"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {imageUrls.map((imageUrl, slideIndex) => (
          <div
            key={`${imageUrl}-${slideIndex}`}
            className="relative w-full shrink-0 snap-center"
          >
            <img
              src={imageUrl}
              alt=""
              loading="lazy"
              className="h-auto max-h-[520px] w-full object-cover"
            />
          </div>
        ))}
      </div>

      {hasMultipleImages && (
        <>
          <div className="absolute right-2 top-2 rounded-full bg-black/60 px-2 py-0.5 text-[11px] font-medium text-white tabular-nums backdrop-blur-sm">
            {activeSlideIndex + 1} / {imageUrls.length}
          </div>

          <div className="pointer-events-none absolute inset-x-0 bottom-2 flex justify-center gap-1.5">
            {imageUrls.map((_, slideIndex) => (
              <span
                key={slideIndex}
                className={`h-1.5 rounded-full transition-all ${
                  slideIndex === activeSlideIndex ? 'w-4 bg-white' : 'w-1.5 bg-white/50'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}
